import PropTypes from 'prop-types';
import useLabels from 'infrastructure/js/hooks/useLabels';
import NameValuePairs from '../../../Common/NameValuePairs/nameValuePairs';
import TextAreaField from 'infrastructure/js/components/controls/TextAreaField/textAreaField';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import {enumTypes, getEnumLabel, getEnumValue} from '../../../../utils/enumHelper';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import CocRangeBar from '../../CoCRangeBar/cocRangeBar';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations';
import CoCAttachments from '../CoCAttachments/cocAttachments';
import CoCResultIndicator from '../../CoCResultIndicator/cocResultIndicator';
import {ResultStatus} from '../../utils/cocHelper';

import './cocSectionListItem.scss';


const isEvenColumn = columnOrder => columnOrder % 2 === 0;

const getValidNumericValue = (item, reduxData) => {
  const raw = reduxData.rawMaterials?.[`id-${item.id}`];
  if (raw === null || raw === undefined || raw === '' || isNaN(raw)) return null;
  return +raw;
};

const getFieldValueStatus = (item, cocData = {}, reduxData = {}) => {
  if (cocData?.passCriteria && !isEvenColumn(item.columnOrder)) return '';

  const value = getValidNumericValue(item, reduxData);
  if (value === null) return '';

  const {minimumValue, maximumValue} = cocData;

  return (value >= minimumValue && value <= maximumValue) ? 'pass' : 'fail';
};

const getResultsStatus = (results, minValue, maxValue) => {
  if (!Array.isArray(results) || results.length === 0) {
    return { status: '',
      minOut: null,
      maxOut: null,
    };
  }

  let hasMissing = false;
  let hasValidNumbers = false;
  const belowMin = [];
  const aboveMax = [];

  for (const item of results) {
    if (item === null || item === undefined || item === '') {
      hasMissing = true;
      continue;
    }

    const num = Number(item);
    if (isNaN(num)) {
      hasMissing = true;
      continue;
    }

    hasValidNumbers = true;

    if (num < minValue) {
      belowMin.push(num);
    } else if (num > maxValue) {
      aboveMax.push(num);
    }
  }

  const hasOutOfRange = belowMin.length > 0 || aboveMax.length > 0;

  if (hasOutOfRange) {
    return {
      status: hasMissing ? ResultStatus.PARTIAL_FAIL : ResultStatus.FAIL,
      minOut: belowMin.length ? Math.min(...belowMin) : null,
      maxOut: aboveMax.length ? Math.max(...aboveMax) : null
    };
  }

  return {
    status: !hasValidNumbers ? '' : (hasMissing ? ResultStatus.PARTIAL_PASS : ResultStatus.PASS),
    minOut: null,
    maxOut: null,
  };
};

const getRangeResultStatus = (cocData = {}, reduxData = {}) => {
  const {rawMaterialsResultFieldsIds = [], minimumValue, maximumValue} = cocData;
  const rawMaterials = reduxData?.rawMaterials || {};

  const results = rawMaterialsResultFieldsIds.map(id => rawMaterials[`id-${id}`]);

  return getResultsStatus(results, minimumValue, maximumValue);
};

const renderRows = (row, rowIndex, index, name, cocData, reduxData, isViewOnly) => {
  return row.map((item, columnIndex) => {
    const itemId = `id-${item.id}`;
    const statusClass = getFieldValueStatus(item, cocData, reduxData);
    const passCriteria = cocData?.passCriteria;

    // const normalizeFn = ((passCriteria === getEnumValue(enumTypes.PASS_CRITERIA)('INDIVIDUAL')) && !isEvenColumn(item.columnOrder)) ? undefined :
    const normalizeFn = (passCriteria && !isEvenColumn(item.columnOrder)) ? undefined :
      Normalize.normalizeFloat(0, 9999.99999, 5);

    return (
      <div key={`key-${index}-${rowIndex}-${columnIndex}`} className="row-material">
        <span className="raw-material-label">{item.displayName}</span>
        <TextField
          className={`raw-material-value ${statusClass}`}
          id={`${name}.[rawMaterials][${item.id}]`}
          name={`${name}.[rawMaterials].${itemId}`}
          normalize={normalizeFn}
          disabled={isViewOnly}
        />
      </div>
    );
  });
}

const renderRawMaterialsResults = (cocData, reduxData, index, name, isViewOnly) => {

  const rawMaterialsFields = cocData?.rawMaterialsFields ?? [];
  return rawMaterialsFields.length > 0 ? (
    <div className="raw-materials-results">
      {rawMaterialsFields.map((row, rowIndex) => (
        <div key={`row-key-${index}-${rowIndex}`} className="raw-material-row">
          {renderRows(row, rowIndex, index, name, cocData, reduxData, isViewOnly)}
        </div>
      ))}
    </div>
  ) : null;
}

const renderComment = (cocData, index, name, isViewOnly) => {
  const hasComment = cocData?.hasComment;

  return hasComment ? (
    <InputSection key={'comment-' + index} label={'Comment'} htmlFor="comment" className="comment-section">
      <TextAreaField id={name + '.[comment]'} name={name + '.[comment]'} className="comment-textField" maxLength="250" disabled={isViewOnly}/>
    </InputSection>
  ) : null;
}

const createCoCSectionData = (labels, cocData) => {
  const sectionData = [ {label: labels.get('set'), value: cocData.setName} ];

  if (cocData.rawMaterialsTested) {
    sectionData.push({label: labels.get('rawMaterialsTested'), value: cocData.rawMaterialsTested});
  }
  if (cocData.passCriteria) {
    sectionData.push({label: labels.get('overallPassCriteria'), value: getEnumLabel(enumTypes.PASS_CRITERIA)(cocData.passCriteria)});
  }

  return sectionData;
}


const CoCSectionListItem = ({
                              name,
                              index: sectionIndex,
                              change,
                              cocData= {},
                              reduxData = {},
                              attachmentsCounters = [],
                              attachmentsFiles = [],
                              onAttachmentUpload = null,
                              onAttachmentUploadValidation = null,
                              onAttachmentRemove = null,
                              onAttachmentClick = null,
                              onAttachmentResultChange = null,
                              isViewOnly = false,
                            }) => {

  const labels = useLabels('mat.forms.cocTestForm.sectionListItem.');

  const cocSectionData = createCoCSectionData(labels, cocData);

  const onAttachmentUploadHandler = (file, type) => {
    onAttachmentUpload?.(file, type, cocData.setId, sectionIndex )
  }

  const onAttachmentRemoveHandler = (file) => {
    onAttachmentRemove?.(file, sectionIndex );
  }

  const onAttachmentClickHandler = (file) => {
    onAttachmentClick?.(file, sectionIndex );
  }

  const onAttachmentResultChangeHandler = (value, file) => {
    onAttachmentResultChange?.(value, file, sectionIndex );
  }



  const renderRawMaterialsSection = () => {
    const hasRawMaterials = cocData?.rawMaterialsResultFieldsIds?.length > 0;
    if (!hasRawMaterials) {
      return null;
    }
    const rangeResult = getRangeResultStatus(cocData, reduxData);
    return (
      <div className='raw-materials-section'>
        <div className='raw-materials-status-indicator'>
          <CocRangeBar label={labels.get('passCriteria')}
                       min={cocData?.minimumValue}
                       max={cocData?.maximumValue}
                       {...rangeResult}
          />

          {[ResultStatus.PASS, ResultStatus.FAIL].includes(rangeResult.status) &&
            <CoCResultIndicator result={rangeResult.status}/>}
        </div>

        {renderRawMaterialsResults(cocData, reduxData, sectionIndex, name, isViewOnly)}
      </div>
    )
  }

  return (
    <div className='coc-section-list-item'>

      <NameValuePairs items={cocSectionData}/>

      <NameValuePairs items={cocData?.optionalFields}/>

      {renderRawMaterialsSection()}

      {renderComment(cocData, sectionIndex, name, isViewOnly)}

      <div className="attachments-section">
        <CoCAttachments
          index={sectionIndex} name={name} change={change}
          isViewOnly={isViewOnly}
          attachmentsCounters={attachmentsCounters}
          attachmentsFiles={attachmentsFiles}
          onAttachmentUpload={onAttachmentUploadHandler}
          onAttachmentUploadValidation={onAttachmentUploadValidation}
          onAttachmentRemove={onAttachmentRemoveHandler}
          onAttachmentClick={onAttachmentClickHandler}
          onAttachmentResultChange={onAttachmentResultChangeHandler}
        />
      </div>
    </div>
  )
}

CoCSectionListItem.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  change: PropTypes.func.isRequired,
  cocData: PropTypes.object,
  reduxData: PropTypes.object,
  attachmentsCounters: PropTypes.array,
  attachmentsFiles: PropTypes.array,
  onAttachmentUpload: PropTypes.func,
  onAttachmentUploadValidation: PropTypes.func,
  onAttachmentRemove: PropTypes.func,
  onAttachmentClick: PropTypes.func,
  onAttachmentResultChange: PropTypes.func,
  isViewOnly: PropTypes.bool,
};

export default CoCSectionListItem;
